// packages/web/src/components/DashboardView.tsx — misma lógica (búsqueda con debounce, cursores, me/learner).
// Cambios: encabezado común, filas de alumnos como tabla, tarjetas por nivel con número y color,
// gráfico con franjas de referencia y ejes, estados técnicos traducidos.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Loader2, Mic, FileText, Shuffle, ChevronRight } from 'lucide-react';
import { api } from '../api';
import { useIdentity } from '../auth/LoginGate';
import { DICTAMEN_LABEL } from '../types';
import { Alert, Button, Card, INPUT_CLASS, PageHeader, StatusBadge, TABLE } from './ui';

const DIMS = [
  { key: 'recuperacion', n: 1, label: 'Recuperación', bar: 'bg-[#2B5C9E]', text: 'text-[#2B5C9E]' },
  { key: 'comprension', n: 2, label: 'Comprensión', bar: 'bg-[#0B7A72]', text: 'text-[#0B7A72]' },
  { key: 'analisis', n: 3, label: 'Análisis', bar: 'bg-[#6A47A6]', text: 'text-[#6A47A6]' },
  { key: 'transferencia', n: 4, label: 'Transferencia', bar: 'bg-[#A0530B]', text: 'text-[#A0530B]' },
] as const;

// Estados de sesión → palabras para el docente.
const SESSION_STATE: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'muted' | 'danger' }> = {
  created: { label: 'Sin empezar', tone: 'muted' },
  warming_up: { label: 'Iniciando', tone: 'info' },
  in_progress: { label: 'En curso', tone: 'info' },
  closed_evidence: { label: 'Cerrada · evidencia suficiente', tone: 'ok' },
  closed_early_high_confidence: { label: 'Cerrada antes · alta confianza', tone: 'ok' },
  closed_time: { label: 'Cerrada · se acabó el tiempo', tone: 'warn' },
  closed_manual: { label: 'Cerrada por el docente', tone: 'muted' },
  abandoned: { label: 'Abandonada', tone: 'danger' },
  pending_teacher_review: { label: 'Pendiente de revisión', tone: 'warn' },
  reviewed: { label: 'Revisada', tone: 'ok' },
};
const sessionState = (s: string) => SESSION_STATE[s] ?? { label: s.replace(/_/g, ' '), tone: 'muted' as const };
const dictamenLabel = (s: string | null) => (s ? (DICTAMEN_LABEL as Record<string, string>)[s.toLowerCase()] ?? s.replace(/_/g, ' ').toLowerCase() : null);

interface DimAverages { recuperacion: number; comprension: number; analisis: number; transferencia: number; global: number; n_pilots: number }
interface SessionRow {
  session_id: string; mongo_oa_id: string; modality: string; started_at: string; closed_at: string | null; current_state: string;
  oa_codigo: string | null; oa_descripcion: string | null; global_score: number | null; state_label: string | null;
}
interface ScorePoint { date: string; global_score: number; state: string }
interface DashboardViewProps { onClose: () => void; onOpenPilot: (sessionId: string) => void }

export function DashboardView({ onClose, onOpenPilot }: DashboardViewProps) {
  const identity = useIdentity();
  const isAdminOrTeacher = identity.activeRole === 'system_admin' || identity.activeRole === 'teacher';
  const isStudent = identity.activeRole === 'student';
  const [selectedLearnerId, setSelectedLearnerId] = useState<string | null>(null);
  const [selectedLearnerLabel, setSelectedLearnerLabel] = useState<string>('');
  const mode = isStudent || selectedLearnerId ? 'detail' : 'search';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        back={isAdminOrTeacher && selectedLearnerId
          ? { label: 'Volver a la búsqueda', onClick: () => { setSelectedLearnerId(null); setSelectedLearnerLabel(''); } }
          : { label: 'Cerrar', onClick: onClose }}
        eyebrow={mode === 'detail' && !isStudent ? 'Dashboard · alumno' : undefined}
        title={mode === 'detail' && selectedLearnerLabel ? selectedLearnerLabel : isStudent ? 'Mi progreso' : 'Dashboard de alumnos'}
        subtitle={mode === 'search' ? 'Busca por nombre, email o nivel para ver el detalle de un alumno.' : 'Promedios por nivel Marzano, evolución y evaluaciones.'}
      />
      {mode === 'search' && isAdminOrTeacher && (
        <AdminSearch onSelect={(l) => {
          setSelectedLearnerId(l.mongo_learner_id);
          setSelectedLearnerLabel(`${l.name ?? l.email ?? l.mongo_learner_id}${l.level_label ? ` · ${l.level_label}` : ''}`);
        }} />
      )}
      {mode === 'detail' && <DetailView learnerId={selectedLearnerId} isSelf={isStudent && !selectedLearnerId} onOpenPilot={onOpenPilot} />}
    </div>
  );
}

type LearnerHit = { mongo_learner_id: string; name: string | null; email: string | null; level_label: string | null; n_pilots: number; last_pilot_at: string | null };

function AdminSearch({ onSelect }: { onSelect: (l: { mongo_learner_id: string; name: string | null; email: string | null; level_label: string | null }) => void }) {
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [levels, setLevels] = useState<string[]>([]);
  const [learners, setLearners] = useState<LearnerHit[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getDashboardLevels().then(({ levels }) => { if (!cancelled) setLevels(levels); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const search = useCallback(async (reset: boolean) => {
    setLoading(true); setError(null);
    try {
      const opts: { q?: string; level_name?: string; cursor?: string } = {};
      if (query.trim()) opts.q = query.trim();
      if (levelFilter) opts.level_name = levelFilter;
      if (!reset && cursor) opts.cursor = cursor;
      const r = await api.searchLearnersAdmin(opts);
      setLearners((prev) => (reset ? r.learners : [...prev, ...r.learners]));
      setCursor(r.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error en búsqueda');
    } finally {
      setLoading(false);
    }
  }, [query, levelFilter, cursor]);

  useEffect(() => {
    const t = setTimeout(() => { void search(true); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, levelFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search aria-hidden className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <label htmlFor="dash-q" className="sr-only">Buscar alumno</label>
          <input id="dash-q" className={INPUT_CLASS + ' !mt-0 pl-9'} type="search" placeholder="Buscar por nombre, email o RUT" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <label htmlFor="dash-level" className="sr-only">Nivel</label>
        <select id="dash-level" className={INPUT_CLASS + ' !mt-0 !w-auto min-w-[200px]'} value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
          <option value="">Todos los niveles</option>
          {levels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {learners.length === 0 && !loading && !error ? (
        <Card><p className="text-sm text-slate-600 text-center py-4">{query || levelFilter ? 'Sin resultados. Ajusta los filtros.' : 'Aún no hay alumnos con evaluaciones registradas.'}</p></Card>
      ) : (
        <div className={TABLE.wrap}>
          <table className={TABLE.table}>
            <thead className={TABLE.thead}>
              <tr><th className={TABLE.th}>Alumno</th><th className={TABLE.th}>Nivel</th><th className={TABLE.th + ' text-right'}>Evaluaciones</th><th className={TABLE.th}>Última</th><th className={TABLE.th}><span className="sr-only">Abrir</span></th></tr>
            </thead>
            <tbody>
              {learners.map((l) => (
                <tr key={l.mongo_learner_id} className={TABLE.tr + ' cursor-pointer'} onClick={() => onSelect(l)}>
                  <td className={TABLE.td}>
                    <button type="button" className="text-left font-semibold text-slate-900 hover:text-marzano-600 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500 rounded" onClick={(e) => { e.stopPropagation(); onSelect(l); }}>
                      {l.name ?? l.email ?? l.mongo_learner_id}
                    </button>
                    {l.name && l.email && <div className="text-[13px] text-slate-600">{l.email}</div>}
                  </td>
                  <td className={TABLE.td + ' text-slate-700'}>{l.level_label ?? '—'}</td>
                  <td className={TABLE.td + ' text-right tabular-nums'}>{l.n_pilots}</td>
                  <td className={TABLE.td + ' text-slate-700'}>{l.last_pilot_at ? new Date(l.last_pilot_at).toLocaleDateString('es-CL') : '—'}</td>
                  <td className={TABLE.td + ' text-right'}><ChevronRight aria-hidden className="inline w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-center">
        {loading ? <Loader2 aria-label="Cargando" className="w-5 h-5 animate-spin text-marzano-600" />
          : cursor ? <Button variant="secondary" size="sm" onClick={() => void search(false)}>Cargar más</Button> : null}
      </div>
    </div>
  );
}

function DetailView({ learnerId, isSelf, onOpenPilot }: { learnerId: string | null; isSelf: boolean; onOpenPilot: (sessionId: string) => void }) {
  const [dimAverages, setDimAverages] = useState<DimAverages | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [evolution, setEvolution] = useState<ScorePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasProfile, setHasProfile] = useState(true);

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    try {
      if (isSelf) {
        const r = await api.getDashboardMe();
        setSessions((prev) => [...prev, ...(r.sessions ?? [])]);
        setCursor(r.next_cursor ?? null);
      } else if (learnerId) {
        const r = await api.getDashboardLearner(learnerId, cursor);
        setSessions((prev) => [...prev, ...r.sessions]);
        setCursor(r.next_cursor);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar más evaluaciones');
    }
  }, [cursor, isSelf, learnerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        if (isSelf) {
          const r = await api.getDashboardMe();
          if (cancelled) return;
          if (!r.has_learner_profile) { setHasProfile(false); return; }
          setDimAverages(r.dim_averages ?? null); setSessions(r.sessions ?? []); setCursor(r.next_cursor ?? null); setEvolution(r.score_evolution ?? []);
        } else if (learnerId) {
          const r = await api.getDashboardLearner(learnerId);
          if (cancelled) return;
          setDimAverages(r.dim_averages); setSessions(r.sessions); setCursor(r.next_cursor); setEvolution(r.score_evolution);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Error al cargar dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSelf, learnerId]);

  if (loading) return <Card><div className="flex justify-center py-8"><Loader2 aria-label="Cargando" className="w-5 h-5 animate-spin text-marzano-600" /></div></Card>;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (!hasProfile && isSelf) return <Card><p className="text-sm text-slate-700 text-center py-4">Tu cuenta no tiene un perfil de alumno asociado en Pioneros. Si esperabas ver tus evaluaciones, contacta al equipo.</p></Card>;

  return (
    <div className="space-y-5">
      {dimAverages && <DimCards dim={dimAverages} />}
      {evolution.length >= 2 && <ScoreChart points={evolution} />}
      <SessionsList sessions={sessions} cursor={cursor} onLoadMore={loadMore} onOpenPilot={onOpenPilot} />
    </div>
  );
}

function DimCards({ dim }: { dim: DimAverages }) {
  const items = useMemo(() => DIMS.map((d) => ({ ...d, value: dim[d.key] })), [dim]);
  return (
    <section aria-labelledby="dims-t" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="dims-t" className="text-base font-semibold text-slate-900">Promedios por nivel Marzano</h2>
        <p className="text-[13px] text-slate-600">{dim.n_pilots} {dim.n_pilots === 1 ? 'evaluación' : 'evaluaciones'} · global <strong className="text-slate-900">{Math.round(dim.global)}</strong>/100</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.key} className="rounded-xl border border-slate-200 bg-white px-4 py-3.5">
            <div className={`text-[13px] font-semibold ${it.text}`}>{it.n} · {it.label}</div>
            <div className="mt-1 flex items-baseline gap-1"><span className="text-[28px] font-bold tabular-nums text-slate-900">{Math.round(it.value)}</span><span className="text-[13px] text-slate-600">/ 100</span></div>
            <div className="mt-2 h-1.5 bg-slate-100 rounded-full" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(it.value)} aria-label={`${it.label} ${Math.round(it.value)} de 100`}>
              <div className={`h-1.5 rounded-full ${it.bar}`} style={{ width: `${Math.max(0, Math.min(100, it.value))}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Mismo dato que el sparkline anterior, con eje Y, franjas de referencia (50 / 80 = umbral por defecto) y etiquetas. */
function ScoreChart({ points }: { points: ScorePoint[] }) {
  const w = 760, h = 200, L = 36, R = 12, T = 12, B = 28;
  const x = (i: number) => L + (i * (w - L - R)) / Math.max(1, points.length - 1);
  const y = (v: number) => T + (1 - v / 100) * (h - T - B);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.global_score).toFixed(1)}`).join(' ');
  return (
    <Card title="Evolución del puntaje global">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" role="img"
        aria-label={`Puntaje global: ${points.map((p) => `${p.date} ${Math.round(p.global_score)}`).join(', ')}`}>
        <rect x={L} y={y(100)} width={w - L - R} height={y(80) - y(100)} className="fill-emerald-50" />
        <rect x={L} y={y(80)} width={w - L - R} height={y(50) - y(80)} className="fill-amber-50" />
        {[0, 50, 80, 100].map((v) => (
          <g key={v}>
            <line x1={L} x2={w - R} y1={y(v)} y2={y(v)} className="stroke-slate-200" />
            <text x={L - 8} y={y(v) + 4} textAnchor="end" className="fill-slate-600 text-[11px]">{v}</text>
          </g>
        ))}
        <path d={path} fill="none" className="stroke-marzano-600" strokeWidth="2.5" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={`${p.date}-${i}`}>
            <circle cx={x(i)} cy={y(p.global_score)} r="5" className="fill-white stroke-marzano-600" strokeWidth="2.5"><title>{`${p.date}: ${Math.round(p.global_score)}/100`}</title></circle>
            <text x={x(i)} y={y(p.global_score) - 10} textAnchor="middle" className="fill-slate-900 text-[12px] font-semibold">{Math.round(p.global_score)}</text>
          </g>
        ))}
        <text x={L} y={h - 6} className="fill-slate-600 text-[11px]">{points[0]?.date}</text>
        <text x={w - R} y={h - 6} textAnchor="end" className="fill-slate-600 text-[11px]">{points[points.length - 1]?.date}</text>
      </svg>
      <p className="text-[13px] text-slate-600">Franja verde: 80 o más (umbral por defecto). Franja ámbar: 50 a 79.</p>
    </Card>
  );
}

function SessionsList({ sessions, cursor, onLoadMore, onOpenPilot }: { sessions: SessionRow[]; cursor: string | null; onLoadMore: () => void; onOpenPilot: (sessionId: string) => void }) {
  if (sessions.length === 0) return <Card><p className="text-sm text-slate-600 text-center py-4">Aún no hay evaluaciones registradas.</p></Card>;
  return (
    <section aria-labelledby="sess-t" className="space-y-3">
      <h2 id="sess-t" className="text-base font-semibold text-slate-900">Evaluaciones</h2>
      <div className={TABLE.wrap}>
        <table className={TABLE.table}>
          <thead className={TABLE.thead}>
            <tr><th className={TABLE.th}>OA</th><th className={TABLE.th}>Objetivo</th><th className={TABLE.th}>Fecha</th><th className={TABLE.th}>Estado</th><th className={TABLE.th + ' text-right'}>Puntaje</th></tr>
          </thead>
          <tbody>
            {sessions.map((s) => {
              const st = sessionState(s.current_state);
              const ModIcon = s.modality === 'oral' || s.modality === 'simplified_1_3' ? Mic : s.modality === 'written' ? FileText : Shuffle;
              return (
                <tr key={s.session_id} className={TABLE.tr + ' cursor-pointer'} onClick={() => onOpenPilot(s.session_id)}>
                  <td className={TABLE.td + ' whitespace-nowrap'}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-mono text-[13px] font-semibold text-marzano-700 bg-marzano-50 rounded px-1.5 py-0.5">{s.oa_codigo ?? '—'}</span>
                      <ModIcon aria-label={`Modalidad ${s.modality}`} className="w-3.5 h-3.5 text-slate-500" />
                    </span>
                  </td>
                  <td className={TABLE.td + ' max-w-[460px]'}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpenPilot(s.session_id); }}
                      className="text-left text-slate-800 line-clamp-2 hover:text-marzano-600 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500 rounded" title={s.oa_descripcion ?? undefined}>
                      {s.oa_descripcion ?? 'OA sin descripción'}
                    </button>
                  </td>
                  <td className={TABLE.td + ' text-slate-700 whitespace-nowrap'}>{new Date(s.started_at).toLocaleDateString('es-CL')}</td>
                  <td className={TABLE.td}><StatusBadge tone={st.tone}>{st.label}</StatusBadge></td>
                  <td className={TABLE.td + ' text-right whitespace-nowrap'}>
                    {s.global_score !== null ? (
                      <span className="flex flex-col items-end"><strong className="text-lg tabular-nums text-slate-900">{Math.round(s.global_score)}</strong>
                        {s.state_label && <span className="text-xs text-slate-600">{dictamenLabel(s.state_label)}</span>}</span>
                    ) : <span className="text-[13px] text-slate-600">Sin reporte</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {cursor && <div className="flex justify-center"><Button variant="secondary" size="sm" onClick={onLoadMore}>Cargar más</Button></div>}
    </section>
  );
}
